import { Button } from '@/components/ui/Button/Button';
import { cva } from 'class-variance-authority';
import { CircleCheck, OctagonAlert, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { type ExternalToast, Toaster as Sonner, toast as sonnerToast } from 'sonner';

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const className = {};
  return (
    <Sonner
      theme='light'
      className='toaster group min-[600px]:!w-[480px]'
      toastOptions={{
        classNames: className,
      }}
      {...props}
    />
  );
};

function toast(toast: Omit<ToastProps, 'id'>, data?: ExternalToast) {
  return sonnerToast.custom((id) => <CustomToast {...toast} id={id} button={toast?.button} />, { duration: 7000, ...data });
}

export function CustomToast(props: ToastProps) {
  const { t } = useTranslation('components', {
    keyPrefix: 'toast',
  });

  const { title, description, button, showIcon = true, showClose = false, variant = 'default' } = props;

  const toastVariants = cva(
    'group pointer-events-auto relative flex items-center justify-between overflow-hidden rounded-surface p-6 text-text-default-inverted w-[388px] max-w-full',
    {
      variants: {
        variant: {
          default: 'bg-fill-status-green',
          destructive: 'group-destructive bg-fill-button-destructive-default',
        },
      },
      defaultVariants: {
        variant: 'default',
      },
    },
  );

  return (
    <div
      className={toastVariants({ variant })}
      role={variant === 'destructive' ? 'alert' : 'status'}
      aria-live={variant === 'destructive' ? 'assertive' : 'polite'}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className='flex gap-4'>
        {showIcon && variant === 'default' && <CircleCheck className='size-6 shrink-0' aria-hidden />}
        {showIcon && variant === 'destructive' && <OctagonAlert className='size-6 shrink-0' aria-hidden />}
        <div className='grid gap-1'>
          {title && <p className='text-heading-s-medium'>{title}</p>}
          {description && <p className='text-body-m-regular'>{description}</p>}
        </div>
      </div>
      {button && (
        <Button variant='secondary' size='m' onClick={button.onClick}>
          {button.label}
        </Button>
      )}
      {showClose && (
        <button
          className='rounded-button absolute top-2 right-2 p-1'
          onClick={(e) => {
            sonnerToast.dismiss(props.id);
            e.stopPropagation();
          }}
          type='button'
        >
          <X className='size-4' />
          <span className='sr-only'>{t('closeButton.accessibleText')}</span>
        </button>
      )}
    </div>
  );
}

export interface ToastProps {
  id: string | number;
  title?: string;
  description?: string;
  showIcon?: boolean;
  showClose?: boolean;
  variant?: 'default' | 'destructive';
  button?: {
    label: string;
    onClick: () => void;
  };
}

// eslint-disable-next-line react-refresh/only-export-components
export { toast, Toaster };
